import { FullCourse, StudentMeetingWithRecordings } from 'server/types';
import { fmtFullTime, fullTimeFormat, minutesToTimeString } from 'shared/dateutil';
import { ReadonlyMinidoc } from '@components/minidoc/readonly-minidoc';
import { Case } from '@components/conditional';
import { Pill } from '@components/pill';
import { MeetingActions } from './meeting-actions';
import {
  IcoCalendar,
  IcoClock,
  IcoInfo,
  IcoPlay,
  IcoSlides,
  LocationIcon,
} from '@components/icons';
import { AddToCalendarButtons } from './add-to-calendar-buttons';
import { RecordingPlayer } from '@course/components/recording-player';
import { MeetingTypeIcons } from './nav-bar';
import { useIntl } from 'shared/intl/use-intl';
import { useMeetingType } from '@components/use-meeting-type';
import { useImageUrl } from 'client/utils/cdn';
import { isPDF } from 'shared/media';
import { useMeetingState } from 'client/lib/hooks/use-meeting-status';
import { useCurrentUser } from 'client/lib/auth';
import { useCurrentTenant } from '@components/router/session-context';
import { IcoZoom } from '@components/icons';
import { MiraseeBadge } from '@components/mirasee-badge';
import { RsvpCard } from './rsvp-card';

interface Props {
  course: Pick<FullCourse, 'level' | 'hideCaptions'>;
  meeting: StudentMeetingWithRecordings;
  isGuide: boolean;
}

function PDFViewer({ file }: { file: NonNullable<StudentMeetingWithRecordings['slidesFile']> }) {
  const url = useImageUrl(file.url);

  return (
    <iframe class="bg-gray-800 w-full rounded-sm aspect-8/12" src={`/pdf.html?pdf=${url}`}></iframe>
  );
}

function InfoText({ text }: { text: string }) {
  return (
    <div class="flex gap-2 items-start text-gray-500 dark:text-gray-200">
      <IcoInfo class="size-5" />
      {text}
    </div>
  );
}

export function MeetingDetail({ course, meeting, isGuide }: Props) {
  const user = useCurrentUser();
  const tenant = useCurrentTenant();
  const meetingState = useMeetingState(meeting);
  const intl = useIntl();
  const meetingTypes = useMeetingType();
  const { status } = meetingState;
  const isMiraseeZoom = tenant.isCore && meeting.type === 'external';
  const isWaitingRoomEnabled = meeting.type === 'zoom' && meeting.zoomHasWaitingRoom;

  return (
    <div class="px-10 mx-auto xl:mx-0 max-w-readable dark:text-gray-200">
      <header class="pb-5 border-b border-gray-200 dark:border-gray-700">
        <div class="flex flex-col align-center mb-6 space-y-3">
          <div>
            {status === 'draft' && <Pill color="yellow">{intl('Draft')}</Pill>}
            {status === 'ended' && <Pill color="red">{intl('COMPLETED')}</Pill>}
            {status === 'inProgress' && (
              <span class="inline-block animate-live">
                <Pill color="green">{intl('ON AIR')}</Pill>
              </span>
            )}
            {status === 'scheduled' && <Pill color="yellow">{intl('NOT STARTED')}</Pill>}
            {status === 'aboutToStart' && <Pill color="yellow">{intl('ABOUT TO START')}</Pill>}
          </div>
          <div class="font-display text-3xl tracking-tight text-gray-900 dark:text-gray-200 mb-9">
            <h1 class="mb-0!">{meeting.title}</h1>
          </div>
        </div>
        <dl class="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-6 my-6 text-gray-500 dark:text-gray-200">
          <dd
            class="flex items-center space-x-2 relative"
            data-tooltip={fmtFullTime(meeting.scheduledAt, {
              includeTimezone: true,
              timeZone: user?.timezone,
            })}
          >
            <IcoCalendar />
            <span>
              {meeting.scheduledAt
                ? fullTimeFormat.format(meeting.scheduledAt)
                : intl('Not scheduled yet')}
            </span>
          </dd>
          <dd class="flex items-center space-x-2">
            <IcoClock />
            <span>{minutesToTimeString(meeting.durationMinutes, intl)}</span>
          </dd>
          <dd class="flex items-center space-x-2">
            {isMiraseeZoom ? (
              <>
                <IcoZoom class="h-4 w-auto" />
                <MiraseeBadge size="sm" />
              </>
            ) : (
              <>
                {MeetingTypeIcons[meeting.type]}
                <span>{meetingTypes[meeting.type]}</span>
              </>
            )}
          </dd>
        </dl>

        {meeting.type === 'inperson' && (
          <div class="flex flex-row items-center space-x-2 text-gray-500 dark:text-gray-200">
            <LocationIcon class="h-4 opacity-75" />
            <span>{meeting.inpersonLocation}</span>
          </div>
        )}

        <Case when={status !== 'ended'}>
          <div class="w-full">
            <MeetingActions meeting={meeting} isGuide={isGuide} />
            {meeting.rsvpEnabled &&
              (status === 'scheduled' || status === 'aboutToStart') &&
              !isGuide && <RsvpCard meetingId={meeting.id} />}
            <p class="inline-flex flex-col lg:flex-row items-center lg:items-start mt-6 text-sm text-zinc-500 dark:text-gray-400 space-y-2 lg:space-y-0">
              <div class="flex flex-col gap-2 items-center">
                <Case when={status === 'scheduled' || status === 'aboutToStart'}>
                  {isGuide && !isWaitingRoomEnabled && (
                    <InfoText
                      text={intl(
                        'Guides can join the meeting anytime. Students will be able to join the meeting after the scheduled start time.',
                      )}
                    />
                  )}
                  {isGuide && isWaitingRoomEnabled && (
                    <>
                      <InfoText
                        text={intl(
                          'Guides can join the meeting anytime. Students can join up to 30 minutes before the scheduled start time.',
                        )}
                      />
                      <InfoText
                        text={intl(
                          `Zoom's Waiting Room is enabled for this meeting. Once you're in Zoom, you can admit students into the meeting.`,
                        )}
                      />
                    </>
                  )}
                  {!isGuide && !meetingState.canJoin && (
                    <InfoText
                      text={
                        meeting.type === 'inperson'
                          ? intl("We're meeting in {timeLeft:string} - hope to see you there.", {
                              timeLeft: meetingState.timeLeft,
                            })
                          : intl('You will be able to join the meeting in {timeLeft:string}.', {
                              timeLeft: meetingState.timeLeft,
                            })
                      }
                    />
                  )}
                </Case>
                {(meeting.type === 'videoconference' || meeting.type === 'presentation') && (
                  <span class="block mt-2">
                    <a
                      class={`${status === 'scheduled'} text-zinc-500 underline`}
                      href="https://support.ruzuku.com/article/750-joining-a-meeting"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {intl('Need help joining the meeting?')}
                    </a>
                  </span>
                )}
              </div>
            </p>
          </div>
        </Case>
      </header>

      {status === 'ended' && meeting.slidesFile && isPDF(meeting.slidesFile.type) && (
        <div class="my-6">
          <header class="flex items-center font-medium text-gray-700 dark:text-gray-200 mb-6">
            <IcoSlides class="h-5 w-5 text-gray-500 dark:text-gray-200 mr-1" />
            <span>{intl('Slides')}</span>
          </header>

          <div class="border border-gray-400 rounded-md">
            <PDFViewer file={meeting.slidesFile} />
          </div>
        </div>
      )}

      <Case when={meeting.recordings.length > 0}>
        <div class="py-8 border-b">
          <header class="flex items-center font-medium text-gray-700 dark:text-gray-200 mb-6">
            <IcoPlay class="h-5 w-5 text-gray-500 dark:text-gray-200 mr-1" />
            <span>{intl('Session Recordings')}</span>
          </header>
          <div class="space-y-6">
            {meeting.recordings.map((recording) => (
              <RecordingPlayer
                key={recording.id}
                recording={recording}
                allowDownload={recording.allowDownload}
                hideCaptions={course.level === 'student' && course.hideCaptions}
              />
            ))}
          </div>
        </div>
      </Case>
      {meeting.description && (
        <ReadonlyMinidoc
          class="pt-8 text-base leading-7"
          id={meeting.id}
          content={meeting.description}
        />
      )}
      {status === 'scheduled' && (
        <footer class="mt-8">
          <AddToCalendarButtons meeting={meeting} />
        </footer>
      )}
    </div>
  );
}
